import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
    Type, inject,
} from '@angular/core';
import { MapItemComponent, MapItem } from '@ui/shared/util-map';
import { FilterModel } from '@ui/shared/util-filter';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { Result } from '@team23/ts-result';
import { SelectedItemState } from '@ui/shared/domain-map';
import { isMobile } from '@ui/shared/ui-common';
import { ApiDataStatus } from '@ui/shared/util-api';
import { FilterState } from '@ui/shared/domain-filter';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-map-sidebar',
    templateUrl: './map-sidebar.component.html',
    styleUrls: ['./map-sidebar.component.scss'],
    standalone: false,
})
export class MapSidebarComponent<MapItemType extends MapItem> {
    @Input({ required: true })
    public mapItems!: Result<Array<MapItemType>, ApiDataStatus>;

    @Input({ required: true })
    public isMinimized!: boolean;

    @Input({ required: true })
    public showMapItems!: boolean;

    @Input()
    public gridContentComponentType!: Type<MapItemComponent<MapItemType>>;

    @Input()
    public listCustomContentComponentType?: Type<MapItemComponent<MapItemType>> | undefined;

    @Input()
    public isMobile = false;

    @Output()
    public readonly mapItemClicked = new EventEmitter<MapItemType>();

    @Output()
    public readonly sidebarMaximized = new EventEmitter();

    @Output()
    public readonly switchToMap = new EventEmitter();

    public showMapItemsAsList = true;

    public loadingStatus = ApiDataStatus.Loading;

    public readonly selectedMapItem$: Observable<MapItemType | null>;
    public readonly shouldShowDetailView$: Observable<boolean>;

    private readonly _categories$ = new BehaviorSubject<FilterModel | undefined>(undefined);

    protected readonly _filterState = inject(FilterState);

    public hasFilter$ = toSignal(this._filterState.activeFilters$.pipe(
        map(filters => filters.size > 0),
    ), { initialValue: false });

    constructor(
        private readonly _selectedMapItemsService: SelectedItemState<MapItemType>,
    ) {
        this.selectedMapItem$ = this._selectedMapItemsService.mapItem$;
        this.shouldShowDetailView$ = this._selectedMapItemsService.showDetailView$;
    }

    @Input()
    public set categories(categories: FilterModel | undefined) {
        this._categories$.next(categories);
    }

    public onMapItemClicked(mapItem: MapItemType): void {
        this._selectedMapItemsService.setMapItem(mapItem);
        this.switchToMap.emit();
    }

    public closeMapItemDetails(): void {
        if (!isMobile()) {
            this._selectedMapItemsService.setMapItem(null);
        }

        this._selectedMapItemsService.setShowDetailView(false);
    }
}
